import { Checkbox } from "@/components/ui/checkbox";
import { Combobox, ENGLISH_COMBOBOX_TEXTS } from "@/components/ui/combobox";
import { OptionList } from "./ds-settings";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SimpleMarkdownRenderer } from "@/components/ui/markdown-renderer";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/datetime-picker";
import { AddressInput } from "@/components/ui/address";
import { FileUpload } from "@/components/ui/file-upload";
import SiretInput from "@/components/ui/input-siret";
import MoneyInput from "@/components/ui/input-money";
import { Button } from "@/components/ui/button";
import { FormulaField } from "@/components/formula/formula-field";
import { ConstantOrFormulaField } from "@/components/formula/constant-or-formula-field";
import { CreatedOption, DocumentCreateAffordance, useDocumentCreator } from "@/components/layout-2/document-creators";
import { FormulaModeToggle } from "@/components/formula/formula-mode-toggle";
import { LiteralInput } from "@/components/formula/literal-input";
import { KeyValueField } from "./key-value-field";
import { DurationGroupField } from "./duration-group-field";
import { isMarked, toFormula, toLiteral } from "@/utils/formula";
import { FieldDataType, FieldGroup } from "@/types/dynamic-detail";
import { DateTime } from "luxon";
import { sluggify } from "@/utils/misc";
import { useFieldArray, type FieldError, type UseFormReturn } from "react-hook-form";
import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { SireneSearchResult } from "@/types/utils/sirene";
import { autoSelection, dropsUnofferedSelection, mayFill, sireneFillValues } from "@/utils/field-autofill";
import { DataService } from "@/services/data";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildDefaultValues, chosenValues, firstSentence, followRows, pickerDate, rowErrorsByKey } from "@/utils/dynamic-form";
import { isFieldVisible } from "@/utils/display-condition";
import { conditionsHold, optionAttributeReader, splitMultipleValues, type OptionAttributeReader } from "@/utils/form-conditions";
import { keepAvailableSelection, resolveOptionsUrl, type DynamicOption } from "@/hooks/use-dynamic-options";
import { hasStaticOptions } from "@/utils/static-options";
import { fieldMemory } from "@/utils/field-memory";
import { resolveFieldOptions } from "@/utils/field-options";
import { formatDurationHHMMSS, formatPercentageInputFromRatio, parsePercentageInputToRatio } from "@/utils/format-time";
import { DurationInput } from "../ui/duration";

type DynamicFormFieldProps = {
    field: FieldDataType;
    form: UseFormReturn<any>;
    dynamicOptions: Record<string, { value: string | null; label: string }[]>;
    loadingOptions: Record<string, boolean>;
    backendFieldErrors?: Record<string, string>;
    /** Context name for file upload paths */
    contextName?: string;
    /** Values `form-prefill` wrote. `locked_when` only locks a field that received one. */
    prefillValues?: Record<string, unknown>;
    /**
     * Form path of the enclosing fieldset row, e.g. `routes.0`. Left out at the root,
     * where a field's path is its own name.
     */
    parentPath?: string;
    /**
     * Descriptor path of the enclosing fieldset, e.g. `routes` — index-free, since
     * every row of a repeatable fieldset shares one descriptor and one option list.
     */
    parentDescriptorPath?: string;
    /**
     * Where a field's documentation goes, when the caller wants it out of the flow.
     *
     * Left out, the description prints under the input as it always has. Given, the
     * caller renders it — as a hover for instance — and the paragraph is dropped, which
     * is the only way a form of forty documented fields fits in a side panel. The
     * decision belongs to the screen rather than to this renderer: the CRM's wide forms
     * have room for the text, a docked inspector does not.
     */
    help?: (field: FieldDataType) => ReactNode;
    /**
     * Refetch the options of a field (by descriptor path), bypassing the server cache. Given,
     * every select / combobox fed by an `options_url` gets a refresh button next to its label;
     * left out (the default), nothing changes. See `useDynamicOptions().reloadOptions`.
     */
    onReloadOptions?: (descriptorPath: string) => Promise<DynamicOption[] | undefined>;
    /** Descriptor paths whose options are being refreshed. */
    reloadingOptions?: Record<string, boolean>;
    /** Descriptor paths whose options failed to load (`useDynamicOptions().failedOptions`): not known. */
    failedOptions?: Record<string, boolean>;
    /**
     * Reads the attributes of a chosen option (`option_attribute`) for `disabled_conditions` and
     * `locked_when`: give the container's reader, the one its `visible_when` / `required_when` read, so the
     * four kinds see the same metadata. `DynamicFormFields` builds it from its own fields when left out, and
     * always inside a fieldset (the conditions of a row name its siblings). A lone field without one reads
     * the options loaded (`dynamicOptions`).
     */
    readAttribute?: OptionAttributeReader;
    /**
     * The fields the conditions of this one name (its container's): a `multiple` choice among them is read
     * item by item (`splitMultipleValues`), as `visible_when` reads it. `DynamicFormFields` passes its own.
     */
    siblingFields?: FieldDataType[];
};

/** Whether a field's options come from a URL the refresh button can ask again. */
export function hasReloadableOptions(field: FieldDataType): boolean {
    const type = field.field_type || "text";
    if (type !== "select" && type !== "combobox") return false;
    return Boolean(field.options_url) && !field.options_search_param && !field.document_options && !hasStaticOptions(field);
}

function ReloadOptionsButton({ label, reloading, disabled, onClick }: { label: string; reloading: boolean; disabled: boolean; onClick: () => void }) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-gray-400 hover:text-gray-700"
            onClick={onClick}
            disabled={disabled || reloading}
            aria-label={`Rafraîchir la liste « ${label} »`}
            title="Rafraîchir la liste"
        >
            <RefreshCw className={cn("h-3 w-3", reloading && "animate-spin")} />
        </Button>
    );
}

/** The values a field's conditions read: its scope, a `multiple` choice of its siblings as a list. */
function conditionScope(form: UseFormReturn<any>, siblings: FieldDataType[] | undefined, parentPath?: string): Record<string, unknown> {
    const scope = ((parentPath ? form.watch(parentPath as any) : form.watch()) as Record<string, unknown> | undefined) ?? {};
    return siblings ? splitMultipleValues(siblings, scope) : scope;
}

function isFieldDisabledByConditions(
    field: FieldDataType,
    form: UseFormReturn<any>,
    readAttribute: OptionAttributeReader,
    siblings: FieldDataType[] | undefined,
    parentPath?: string
): boolean {
    const conditions = field.disabled_conditions;
    if (!conditions?.length) {
        return false;
    }
    // Same evaluator and option reader as `visible_when` / `required_when` (and as `form-action`), on the field's scope.
    return conditionsHold(conditions, conditionScope(form, siblings, parentPath), readAttribute);
}

function isFieldLocked(
    field: FieldDataType,
    form: UseFormReturn<any>,
    prefillValues: Record<string, unknown> | undefined,
    readAttribute: OptionAttributeReader,
    siblings: FieldDataType[] | undefined,
    parentPath?: string
): boolean {
    if (!field.locked_when?.length) return false;
    const prefill = prefillValues?.[field.field_name];
    if (prefill == null || (typeof prefill === "string" && prefill.trim() === "")) return false;
    return conditionsHold(field.locked_when, conditionScope(form, siblings, parentPath), readAttribute);
}

/**
 * A SIRET whose Sirene establishment fills other fields (`Field.siret_fills`), through the
 * `/utils/siret` lookup. A SIRET typed overwrites them; a SIRET already there (prefill, step shown
 * again) only fills the empty ones. A SIRET typed afterwards that the directory does not know clears what
 * the previous lookup filled, unless it was changed since (a value typed is never lost).
 */
function SiretLookupInput({ field, form, disabled }: { field: FieldDataType; form: UseFormReturn<any>; disabled: boolean }) {
    /**
     * Whether the SIRET was typed in this opening of the form. In the form's field memory too: a container
     * that reopens its form (`resetFieldMemory`) forgets it, so a SIRET prefilled then only fills empty fields.
     */
    const typedMemory = fieldMemory<boolean>(form, `${field.field_name}:typed`);
    const typed = {
        get current(): boolean {
            return typedMemory.get() ?? false;
        },
        set current(value: boolean) {
            typedMemory.set(value);
        }
    };
    // A SIRET on screen again (a step shown again) is one already there: its lookup only fills empty fields.
    useEffect(() => {
        typedMemory.set(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * The values the last lookup wrote, by field: what a SIRET not found takes back. Kept in the form's field
     * memory (keyed by this field's name), not in the component, so a field remounted (a form in steps shows
     * only the step on screen) still takes back the previous establishment's values.
     */
    const memory = fieldMemory<Record<string, string>>(form, field.field_name);
    const filled = {
        get current(): Record<string, string> {
            return memory.get() ?? {};
        },
        set current(value: Record<string, string>) {
            memory.set(value);
        }
    };
    const [found, setFound] = useState<SireneSearchResult | null | undefined>(undefined);
    /** Clears what the previous lookup wrote into `targets`, unless it was changed since. */
    const takeBack = (targets: string[]) => {
        targets.forEach(target => {
            if (form.getValues(target) === filled.current[target]) form.setValue(target, "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        });
    };
    const onFound = (result: SireneSearchResult | null | undefined, siret?: string) => {
        // A lookup answering for a SIRET no longer in the field (a slower one for an earlier number) is ignored.
        const digits = (value: unknown) => String(value ?? "").replace(/\D/g, "");
        if (result !== undefined && siret !== undefined && digits(siret) !== digits(form.getValues(field.field_name))) return;
        setFound(result);
        if (result === null && typed.current) {
            takeBack(Object.keys(filled.current));
            filled.current = {};
            return;
        }
        if (!result) return;
        const fills = sireneFillValues(field.siret_fills, result as unknown as Record<string, unknown>);
        // Another establishment found: what the previous one filled and this one does not provide is not
        // its data (a name, an address): taken back too, unless changed since.
        if (typed.current) takeBack(Object.keys(filled.current).filter(target => !(target in fills)));
        const written: Record<string, string> = {};
        Object.entries(fills).forEach(([target, value]) => {
            const current = form.getValues(target);
            if (current === value) {
                // Still the value a lookup wrote: still taken back by a SIRET not found.
                if (filled.current[target] === value) written[target] = value;
                return;
            }
            if (!mayFill(typed.current, current)) return;
            form.setValue(target, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
            written[target] = value;
        });
        filled.current = written;
    };
    return (
        <div className="space-y-1">
            <SiretInput
                id={field.field_name}
                placeholder="843 382 409 00020"
                value={form.watch(field.field_name as any) || ""}
                onValueChange={value => {
                    typed.current = true;
                    // Dirty: a value typed wins over a prefill answering later (`form-prefill` skips dirty fields).
                    form.setValue(field.field_name as any, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                }}
                onlineCheck
                setSireneSearchResult={onFound}
                disabled={disabled}
            />
            {found ? (
                <p className="text-xs text-muted-foreground">
                    {[found.denominationunitelegale || found.l1_adressage_unitelegale, found.libellecommuneetablissement, found.codepostaletablissement].filter(Boolean).join(" • ")}
                </p>
            ) : found === null ? (
                <p className="text-xs text-muted-foreground">No establishment found in the Sirene directory (expected if it was created recently).</p>
            ) : null}
        </div>
    );
}

function joinPath(prefix: string | undefined, name: string): string {
    return prefix ? `${prefix}.${name}` : name;
}

function isRequiredField(field: FieldDataType): boolean {
    return field.required === true || field.validators?.some(v => v.type === "required") === true;
}

/** Shapes the picklist endpoints answer with: a bare list, or a paginated envelope. */
type RemoteOption = { value?: string | number; id?: string | number; label?: string; name?: string };
type RemoteOptions = RemoteOption[] | { results?: RemoteOption[] };

/**
 * Combobox over a referential too large to download: it asks the endpoint as the operator types.
 *
 * The selected option is kept aside from the search results, otherwise the trigger would fall back
 * to "unknown value" as soon as a new search drops the chosen row out of the list.
 */
function SearchableCombobox({
    field,
    url,
    name,
    value,
    disabled,
    onValueChange
}: {
    field: FieldDataType;
    /** `options_url` with its dependent parameters resolved. */
    url: string | undefined;
    name: string;
    value: string;
    disabled: boolean;
    onValueChange: (value: string) => void;
}) {
    const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
    const [selected, setSelected] = useState<{ value: string; label: string } | null>(null);
    const [query, setQuery] = useState("");

    useEffect(() => {
        const searchParam = field.options_search_param;
        if (!url || !searchParam) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            DataService.client
                .url(url)
                .query({ [searchParam]: query })
                .get()
                .json<RemoteOptions>()
                .then(data => {
                    if (cancelled) return;
                    const results = Array.isArray(data) ? data : (data.results ?? []);
                    setOptions(
                        results.map(item => {
                            const value = String(item.value ?? item.id ?? "");
                            return { value, label: item.label ?? item.name ?? value };
                        })
                    );
                })
                .catch(() => {
                    if (!cancelled) setOptions([]);
                });
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [url, field.options_search_param, query]);

    const known = selected && !options.some(option => option.value === selected.value) ? [selected, ...options] : options;

    return (
        <Combobox
            name={name}
            options={known}
            value={value}
            shouldFilter={false}
            onInputValueChange={setQuery}
            onValueChange={next => {
                setSelected(known.find(option => option.value === next) ?? null);
                onValueChange(next);
            }}
            className="min-h-[2.5rem] rounded border px-2 py-1"
            placeholder="Select an option"
            texts={ENGLISH_COMBOBOX_TEXTS}
            disabled={disabled}
        />
    );
}

/**
 * Renders a single form field dynamically based on its field_type.
 * Shared between ActionModal and DynamicObjectForm.
 */
export function DynamicFormField({
    field,
    form,
    dynamicOptions,
    loadingOptions,
    backendFieldErrors = {},
    contextName = "form",
    parentPath,
    parentDescriptorPath,
    help,
    prefillValues,
    onReloadOptions,
    reloadingOptions,
    failedOptions,
    readAttribute,
    siblingFields
}: DynamicFormFieldProps) {
    const label = field.label || field.field_name;
    const type = field.field_type || "text";
    const colSpan = field.large_field ? 2 : 1;
    const description = field.description || "";
    const readOption = readAttribute ?? optionAttributeReader([], dynamicOptions);
    const disabledByConditions = isFieldDisabledByConditions(field, form, readOption, siblingFields, parentPath);
    const locked = isFieldLocked(field, form, prefillValues, readOption, siblingFields, parentPath);
    const disabled = !!field.disabled || disabledByConditions || locked;
    // `name` addresses the value in the form, `descriptorPath` addresses the field in
    // the descriptor. They are the same string outside a fieldset.
    const name = joinPath(parentPath, field.field_name);
    const descriptorPath = joinPath(parentDescriptorPath, field.field_name);

    /**
     * A field the server declared switchable holds either a value or an expression, and
     * which of the two is read off the stored value: an expression opens with `=`.
     *
     * Nowhere is that mode kept in a state of this component, and that is the point. The
     * drawer writes on every keystroke and remounts this field whenever visibility is
     * recomputed, so a mode held here would survive neither — while a mode carried by the
     * value is exactly as durable as the value, which is what the author saved.
     */
    const switchable = field.formula?.switchable === true;
    const storedValue = switchable ? form.watch(name as any) : undefined;
    const isExpression = !!field.formula && (!switchable || isMarked(storedValue));

    /**
     * `formula.expects_from`: the sibling whose chosen option says what type this value is.
     * Read off that sibling's option list — the server put `type` on each option — so the
     * renderer names no field and no block.
     */
    const expectsFrom = field.formula?.expects_from;
    const siblingValue = expectsFrom ? form.watch(joinPath(parentPath, expectsFrom)) : undefined;
    const valueType = expectsFrom
        ? ((dynamicOptions[joinPath(parentDescriptorPath, expectsFrom)] ?? []) as { value: string | null; type?: unknown }[]).find(option => option.value === siblingValue)?.type
        : undefined;

    // `document_create`: an entry this field just created, shown as chosen until the
    // document it was written to answers with it in the list.
    const creator = useDocumentCreator(field.document_options ? field.document_create : null);
    const [created, setCreated] = useState<CreatedOption | null>(null);

    // The field's effective options, whatever their source (served with the descriptor or fetched): one
    // resolver for the list drawn, its loading / error state and the drop of what is no longer offered.
    const resolved = resolveFieldOptions(field, descriptorPath, { dynamicOptions, loadingOptions, failedOptions });
    const listed = resolved.options;
    const options = created && !listed.some((option: { value: string | null } | string) => (typeof option === "string" ? option : option.value) === created.value) ? [...listed, created] : listed;
    const formattedOptions = options.map((opt: { value: string | null; label: string; disabled?: boolean; description?: string; group?: string | null } | string) => {
        let value = typeof opt === "string" ? opt : opt.value;
        let label = typeof opt === "string" ? opt : opt.label;
        if (label === null) {
            label = value || "Untitled";
        }
        if (value === null) {
            value = "__null__";
        }
        // Listed but not selectable, with the reason (`options_handler` of `form-options`).
        const extra =
            typeof opt === "string" ? {} : { ...(opt.disabled ? { disabled: true } : {}), ...(opt.description ? { description: opt.description } : {}), ...(opt.group ? { group: opt.group } : {}) };

        return { value, label, ...extra };
    });
    // Only this field's own list can be loading: a list the server served (static `options`, a step's final
    // list) is there already, whatever a fetch under the same path does (the form-wide descriptor of a field a
    // step serves differently, say).
    const isLoading = resolved.loading;
    const reloadable = Boolean(onReloadOptions) && hasReloadableOptions(field);
    const reloading = Boolean(reloadingOptions?.[descriptorPath]);
    // The selection survives a refresh when its value is still offered, and is dropped otherwise.
    const reload = async () => {
        if (!onReloadOptions) return;
        const fresh = await onReloadOptions(descriptorPath);
        if (!fresh) return;
        const current = form.getValues(name);
        const kept = keepAvailableSelection(current, fresh, field.multiple);
        if (kept !== current) form.setValue(name, kept, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    };
    const required = isRequiredField(field);
    // `getFieldState` resolves a dotted path, so a child of a fieldset row reads its
    // own error rather than the whole group's.
    const { error, isTouched } = form.getFieldState(name, form.formState);
    const showError = !!error && (isTouched || form.formState.isSubmitted);

    // A field disabled by its conditions must not keep a previously chosen value (e.g. parent while
    // creating a group). Static `field.disabled` is read-only display: it keeps its prefilled value.
    useEffect(() => {
        if (!disabledByConditions) {
            return;
        }
        const current = form.getValues(name as any);
        if (current == null || current === "") {
            return;
        }
        form.setValue(name as any, null, { shouldValidate: true, shouldDirty: true });
    }, [disabledByConditions, form, name]);

    // `auto_select_single`: once the list is final (served, even empty, or loaded), a value it no longer
    // offers as selectable (the list followed another choice above) is dropped, and the only selectable
    // option is chosen while the field is empty.
    const autoLoaded = Boolean(field.auto_select_single) && !disabled && !isLoading && resolved.final;
    const autoKey = autoLoaded ? JSON.stringify(formattedOptions.map(option => [option.value, Boolean(option.disabled)])) : "";
    useEffect(() => {
        if (!autoLoaded) return;
        const next = autoSelection(form.getValues(name as any), formattedOptions);
        if (next === undefined) return;
        form.setValue(name as any, next, { shouldValidate: true, shouldDirty: true });
    }, [autoKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Options that are the only valid list (`options_params` `$field`, or `keep_offered_only` on a list a
    // step serves, even empty): once the list is there, a selection it no longer offers is dropped, in
    // forms, wizard steps and action dialogs alike, whatever the source (served, static or fetched). What
    // follows the field (`rows_from` rows) follows it. The server refuses it anyway (write checks).
    // Only a list known to be final: one the server served, or a load that succeeded (a failed load leaves
    // no list, `failedOptions`); never while loading nor after a failure.
    const dependentLoaded = dropsUnofferedSelection(field) && !disabled && !isLoading && resolved.final;
    const dependentKey = dependentLoaded ? JSON.stringify(formattedOptions.map(option => [option.value, Boolean(option.disabled)])) : "";
    useEffect(() => {
        if (!dependentLoaded) return;
        const current = form.getValues(name as any);
        const kept = keepAvailableSelection(current, options, field.multiple);
        if (kept !== current) form.setValue(name as any, kept, { shouldValidate: true, shouldDirty: true });
    }, [dependentKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // A `display_condition` is read against the field's own scope: the enclosing
    // fieldset row when there is one, the form root otherwise. Watched only when the
    // descriptor carries a condition, so forms without one keep their subscriptions.
    const conditionScope = field.display_condition ? (parentPath ? form.watch(parentPath as any) : form.watch()) : undefined;
    if (field.display_condition && !isFieldVisible(field, conditionScope as Record<string, unknown> | undefined)) {
        return null;
    }

    if (type === "alert" || type === "alert-info" || type === "alert-warning" || type === "alert-error") {
        const alertValue = field.value == null ? "" : String(field.value);
        const alertClass =
            type === "alert-error"
                ? "bg-red-100 text-red-800"
                : type === "alert-warning"
                  ? "bg-amber-100 text-amber-800"
                  : type === "alert-info"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-700";
        return (
            <div key={name} className={`col-span-${colSpan} rounded-md p-2 ${alertClass}`}>
                <SimpleMarkdownRenderer content={alertValue} className="[&>*:last-child]:mb-0" />
            </div>
        );
    }

    if (type === "fieldset") {
        const fieldsetProps = {
            field,
            form,
            name,
            descriptorPath,
            dynamicOptions,
            loadingOptions,
            backendFieldErrors,
            contextName,
            help,
            onReloadOptions,
            reloadingOptions,
            failedOptions
        };
        return field.multiple ? <RepeatableFieldset {...fieldsetProps} /> : <FieldsetGroup {...fieldsetProps} />;
    }

    if (field.hidden) {
        return null;
    }

    if (type === "checkbox") {
        return (
            <div key={name} className={`flex flex-col justify-center gap-2 col-span-${colSpan}`}>
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={name}
                        checked={form.watch(name as any) || false}
                        onCheckedChange={value => form.setValue(name as any, !!value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                        disabled={disabled}
                    />
                    <label htmlFor={name} className="text-sm font-medium">
                        {label}
                        {required && <sup className="text-xs text-red-500">*</sup>}
                    </label>
                    {help?.(field)}
                </div>
                {!help && description && <SimpleMarkdownRenderer content={description} className="text-xs text-gray-500" />}
                {backendFieldErrors[name] && <span className="text-xs text-red-500">{backendFieldErrors[name]}</span>}
                {showError && <span className="text-xs text-red-500">{(error?.message as string) || ""}</span>}
            </div>
        );
    }

    return (
        <div key={name} className={`flex flex-col gap-1 col-span-${colSpan}`}>
            <div className="flex items-center gap-1">
                <FormLabel className="text-sm font-medium" htmlFor={name} required={required}>
                    {label}
                </FormLabel>
                {help?.(field)}
                {reloadable && <ReloadOptionsButton label={label} reloading={reloading} disabled={disabled} onClick={reload} />}
                {switchable && !field.disabled && (
                    <FormulaModeToggle
                        id={name}
                        expression={isExpression}
                        /* One write, and it carries the value across: `toFormula` quotes
                           what was typed, `toLiteral` unquotes it. A click that cleared the
                           box would be a click nobody dares. */
                        onToggle={() => form.setValue(name as any, isExpression ? toLiteral(storedValue) : toFormula(storedValue ?? ""), { shouldValidate: true, shouldTouch: true })}
                    />
                )}
            </div>
            {type === "key_value" ? (
                /* Before the formula branch: a mapping that declares a formula declares it
                   for each of its values, and each row renders that input itself. */
                <KeyValueField field={field} form={form} name={name} label={label} description={description} disabled={disabled} />
            ) : isExpression && field.formula?.constant_entry && !switchable ? (
                /* Still an expression in storage; offered as « Fixed value | Formula » so a
                   value is typed as it is, and quoted on the way in. */
                <ConstantOrFormulaField
                    id={name}
                    formula={field.formula}
                    value={form.watch(name) ?? ""}
                    type={typeof valueType === "string" ? valueType : null}
                    disabled={disabled}
                    large={field.large_field}
                    label={label}
                    description={description}
                    onChange={value => form.setValue(name, value, { shouldValidate: true, shouldTouch: true })}
                />
            ) : isExpression && field.formula ? (
                /* The descriptor said this holds an expression, so the renderer stops reading
                   `field_type` here: a formula is not a `text` that happens to be long. No
                   field name, no node type, no list of fields that are formulas — which is
                   what lets a handler declare a new one and have it rendered without an
                   interface change. A *switchable* field reaches this branch only while its
                   value is marked, so in its other mode it is the input of its declared type
                   again, and everything below applies to it unchanged. */
                <FormulaField
                    id={name}
                    formula={field.formula}
                    value={form.watch(name as any) ?? ""}
                    disabled={disabled}
                    large={field.large_field}
                    label={label}
                    description={description}
                    onChange={value => form.setValue(name as any, value, { shouldValidate: true, shouldTouch: true })}
                />
            ) : switchable ? (
                <LiteralInput
                    id={name}
                    value={storedValue}
                    large={field.large_field}
                    required={required}
                    disabled={disabled}
                    onChange={value => form.setValue(name as any, value, { shouldValidate: true, shouldTouch: true })}
                />
            ) : type === "textarea" ? (
                <Textarea id={name} {...form.register(name as any)} disabled={disabled} required={required} className="rounded border px-2 py-1" />
            ) : type === "json" ? (
                <Textarea
                    id={name}
                    {...form.register(name as any)}
                    disabled={disabled}
                    required={required}
                    className="min-h-[180px] rounded border px-2 py-1 font-mono text-xs"
                    placeholder='{"slots":[{"format":"fc","duration_minutes":120}]}'
                />
            ) : (type === "combobox" || type === "select") && !field.multiple && !isLoading && field.option_display === "option_list" ? (
                <OptionList
                    id={name}
                    label={field.label || name}
                    options={formattedOptions}
                    value={form.watch(name)?.toString() || ""}
                    disabled={disabled}
                    onChange={value => form.setValue(name as any, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                />
            ) : type === "combobox" ? (
                field.options_search_param ? (
                    <SearchableCombobox
                        field={field}
                        url={field.options_params ? resolveOptionsUrl(field, form.watch()) : field.options_url}
                        name={name}
                        value={form.watch(name)?.toString() || ""}
                        disabled={disabled}
                        onValueChange={value => form.setValue(name as any, value === "" ? null : value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                    />
                ) : isLoading ? (
                    <div className="rounded border px-2 py-1 text-xs text-gray-400">Loading...</div>
                ) : (
                    <Combobox
                        name={name}
                        options={formattedOptions}
                        value={(form.watch(name) === null ? "__null__" : form.watch(name)?.toString()) || ""}
                        onValueChange={value => {
                            const _value = value === "__null__" ? null : value;
                            form.setValue(name as any, _value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                        }}
                        className="min-h-[2.5rem] rounded border px-2 py-1"
                        placeholder="Select an option"
                        multiple={field.multiple}
                        maxSelected={field.max_selected}
                        disabled={disabled}
                        allowCustomValue={field.allow_custom_value}
                        texts={ENGLISH_COMBOBOX_TEXTS}
                    />
                )
            ) : type === "select" ? (
                isLoading ? (
                    <div className="rounded border px-2 py-1 text-xs text-gray-400">Loading...</div>
                ) : (
                    <Select
                        value={(form.watch(name) === null ? "__null__" : form.watch(name)?.toString()) || ""}
                        onValueChange={value => {
                            const _value = value === "__null__" ? null : value;
                            form.setValue(name as any, _value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                        }}
                        disabled={disabled}
                    >
                        <SelectTrigger id={name} className="rounded border px-2 py-1">
                            <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                            {formattedOptions.map((opt: any) => (
                                <SelectItem key={opt.value} value={opt.value.toString()} disabled={Boolean(opt.disabled)}>
                                    {opt.label || opt.value}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )
            ) : type === "datetime" || type === "datetime-local" ? (
                <DateTimePicker
                    date={form.watch(name as any) ? DateTime.fromISO(form.watch(name as any)) : undefined}
                    setDate={value => form.setValue(name as any, value?.toISO({ suppressMilliseconds: true }) || "", { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                    className="w-full"
                    disabled={disabled}
                    allowClear={!required}
                />
            ) : type === "date" ? (
                // The CRM's date picker (typed or picked, weekends included): the form keeps an ISO date (`yyyy-MM-dd`).
                <DatePicker
                    id={name}
                    date={pickerDate(form.watch(name as any))}
                    setDate={value => form.setValue(name as any, value?.toISODate() ?? "", { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                    className="w-full"
                    placeholder="jj/mm/aaaa"
                    disabled={disabled}
                    allowClear={!required}
                    enableWeekend
                />
            ) : type === "address" ? (
                <AddressInput
                    address={form.watch(name as any) ? JSON.parse(form.watch(name as any)) : undefined}
                    onAddressChange={value => form.setValue(name as any, JSON.stringify(value) || "", { shouldValidate: true, shouldTouch: true })}
                    className="w-full"
                    disabled={disabled}
                />
            ) : type === "siret" && field.siret_fills ? (
                <SiretLookupInput field={field} form={form} disabled={disabled} />
            ) : type === "siret" ? (
                <SiretInput
                    id={field.field_name}
                    placeholder="843 382 409 00020"
                    value={form.watch(field.field_name as any) || ""}
                    // Dirty: a value typed wins over a prefill answering later (`form-prefill` skips dirty fields).
                    onValueChange={value => form.setValue(field.field_name as any, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })}
                    disabled={disabled}
                />
            ) : type === "file" ? (
                <FileUpload
                    bucketName="walter-inscription"
                    folderPath={`__${contextName}/${sluggify(field.field_name)}/${DateTime.now().toMillis()}`}
                    url={form.watch(name as any) || ""}
                    onUpload={value => form.setValue(name as any, value || "", { shouldValidate: true, shouldTouch: true })}
                />
            ) : type === "currency" ? (
                <MoneyInput id={name} value={form.watch(name as any) || 0} onValueChange={value => form.setValue(name as any, value || 0, { shouldValidate: true, shouldTouch: true })} disabled={disabled} />
            ) : type === "boolean" ? (
                <Select
                    value={form.watch(name) ? "true" : "false"}
                    onValueChange={value => {
                        const _value = value === "__null__" ? null : value;
                        form.setValue(name as any, _value === "true" ? true : _value === "false" ? false : null, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                    }}
                    disabled={disabled}
                >
                    <SelectTrigger id={name} className="rounded border px-2 py-1">
                        <SelectValue placeholder="Oui/Non" />
                    </SelectTrigger>
                    <SelectContent>
                        {[
                            { value: "true", label: "Oui" },
                            { value: "false", label: "Non" }
                        ].map((opt: any) => (
                            <SelectItem key={opt.value} value={opt.value.toString()}>
                                {opt.label || opt.value}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            ) : type === "duration" ? (
                <DurationInput
                    value={form.watch(name) != null ? formatDurationHHMMSS(Number(form.watch(name))) : ""}
                    onChange={val => form.setValue(name as any, val, { shouldValidate: true, shouldTouch: true })}
                />
            ) : type === "percentage" ? (
                <Input
                    id={name}
                    type="number"
                    step="0.01"
                    value={form.watch(name) !== "" && form.watch(name) != null ? formatPercentageInputFromRatio(Number(form.watch(name))) : ""}
                    onChange={e => {
                        const ratioValue = parsePercentageInputToRatio(e.target.value);
                        form.setValue(name as any, ratioValue ?? "", {
                            shouldValidate: true,
                            shouldTouch: true
                        });
                    }}
                    required={required}
                    disabled={disabled}
                />
            ) : (
                // `step="any"`: a number field takes decimals (an amount); the server validates the value.
                <Input
                    id={name}
                    type={type}
                    step={type === "number" ? "any" : undefined}
                    {...form.register(name as any)}
                    required={required}
                    disabled={disabled}
                />
            )}
            {creator && !disabled && (
                <DocumentCreateAffordance
                    id={name}
                    creator={creator}
                    onCreated={option => {
                        setCreated(option);
                        form.setValue(name, option.value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                    }}
                />
            )}
            {!help && description && <SimpleMarkdownRenderer content={description} className="text-xs text-gray-500" />}
            {resolved.failed && <span className="text-xs text-gray-500">The options could not be loaded.</span>}
            {backendFieldErrors[name] && <span className="text-xs text-red-500">{backendFieldErrors[name]}</span>}
            {showError && <span className="text-xs text-red-500">{(error?.message as string) || ""}</span>}
        </div>
    );
}

/** Fields of one grid, as `DynamicFormFields` receives them. */
type FieldListProps = Omit<DynamicFormFieldProps, "field"> & {
    fields: FieldDataType[];
    /** Layout of the grid, so a caller with a narrower panel can ask for one column. */
    className?: string;
};

type FieldRun = { group: FieldGroup | null; fields: FieldDataType[] };

/**
 * Consecutive fields sharing a `field_group.key`, as runs.
 *
 * Consecutive rather than gathered from anywhere in the list: the descriptor already
 * decides the order fields are shown in, and a grouping that reordered them would make
 * the form's layout depend on something other than the descriptor it is derived from.
 */
export function fieldRuns(fields: FieldDataType[]): FieldRun[] {
    const runs: FieldRun[] = [];
    fields.forEach(field => {
        const group = field.field_group ?? null;
        const last = runs[runs.length - 1];
        if (group && last?.group && last.group.key === group.key) {
            last.fields.push(field);
            return;
        }
        runs.push({ group, fields: [field] });
    });
    return runs;
}

/**
 * A run of grouped fields, drawn as one block with the operator between its members.
 *
 * The operator is the point of the whole mechanism. Inputs stacked with nothing between
 * them read as conditions that add up — which is exactly what a reader concluded about
 * the three claim rows of a qualification router — and no arrangement of boxes says
 * « or ». The word therefore comes from the server, and it is drawn between the members
 * where the eye already is rather than explained in a sentence above them.
 *
 * A group without an operator is a section of the form: fields read together under one heading (the
 * company, the convention's signer), laid out in the form's own two-column grid.
 */
export function FieldGroupBlock({ group, fields, ...props }: Omit<DynamicFormFieldProps, "field"> & { group: FieldGroup; fields: FieldDataType[] }) {
    if (group.compose === "duration") {
        return (
            <DurationGroupField
                group={group}
                fields={fields}
                form={props.form}
                pathOf={field => joinPath(props.parentPath, field.field_name)}
                disabled={fields.every(field => field.disabled)}
            />
        );
    }
    if (!group.joiner) {
        return (
            <section className="col-span-2 space-y-3 pt-2" aria-label={group.label ?? undefined} data-testid={`field-group-${group.key}`}>
                {group.label && <h3 className="border-b pb-1.5 text-sm font-semibold text-gray-900">{group.label}</h3>}
                <div className="grid grid-cols-2 gap-4">
                    {fields.map(field => (
                        <DynamicFormField key={field.field_name} field={field} {...props} />
                    ))}
                </div>
            </section>
        );
    }
    return (
        <div className="col-span-2 flex flex-col gap-1.5 rounded-md border border-dashed border-input p-3" data-testid={`field-group-${group.key}`}>
            {group.label && <span className="text-xs font-medium text-gray-600">{group.label}</span>}
            {fields.map((field, index) => (
                <Fragment key={field.field_name}>
                    {index > 0 && group.joiner && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400" data-testid={`field-group-joiner-${group.key}`}>
                            {group.joiner}
                        </span>
                    )}
                    <DynamicFormField field={field} {...props} />
                </Fragment>
            ))}
        </div>
    );
}

/**
 * A list of fields as one grid, honouring the groups the descriptor declares.
 *
 * Every grid of dynamic fields goes through here so that a group is rendered wherever the
 * server puts one, rather than only in the one place that happened to need it first. A
 * descriptor that groups nothing renders exactly as it did before.
 */
export function DynamicFormFields({ fields, className = "grid grid-cols-2 gap-4", ...rest }: FieldListProps) {
    // The option attributes the conditions of these fields read: their siblings', at this descriptor path.
    const ownReader = useMemo(
        () => optionAttributeReader(fields, rest.dynamicOptions, rest.parentDescriptorPath),
        [fields, rest.dynamicOptions, rest.parentDescriptorPath]
    );
    const props = { ...rest, readAttribute: rest.parentDescriptorPath ? ownReader : (rest.readAttribute ?? ownReader), siblingFields: fields };
    return (
        <div className={className}>
            {fieldRuns(fields).map((run, index) =>
                run.group ? (
                    <FieldGroupBlock key={`group:${run.group.key}:${index}`} group={run.group} fields={run.fields} {...props} />
                ) : (
                    <Fragment key={`fields:${index}`}>
                        {run.fields.map(field => (
                            <DynamicFormField key={field.field_name} field={field} {...props} />
                        ))}
                    </Fragment>
                )
            )}
        </div>
    );
}

/**
 * The line a composite prints under its title.
 *
 * A `fieldset` is the one field whose *rule of combination* cannot be guessed from the
 * inputs inside it — how the rows of a router are matched against a call, how the claims
 * of one row add up — and a hover is the wrong home for a rule that can surprise an
 * author who never hovers. So when the screen has moved documentation into a bubble, the
 * first sentence of the server's description stays on screen and the rest goes in the
 * bubble; a screen that prints descriptions in full is left as it was.
 */
function FieldsetHint({ description, help }: { description: string; help: boolean }) {
    if (!description) return null;
    if (!help) return <SimpleMarkdownRenderer content={description} className="text-xs text-gray-500" />;
    return <p className="px-1 text-xs text-gray-500">{firstSentence(description)}</p>;
}

type FieldsetProps = {
    field: FieldDataType;
    form: UseFormReturn<any>;
    /** Form path of the group itself, e.g. `rules.0.all_of`. */
    name: string;
    /** Descriptor path of the group itself, e.g. `rules.all_of`. */
    descriptorPath: string;
    dynamicOptions: Record<string, { value: string | null; label: string }[]>;
    loadingOptions: Record<string, boolean>;
    backendFieldErrors: Record<string, string>;
    contextName: string;
    help?: (field: FieldDataType) => ReactNode;
    onReloadOptions?: DynamicFormFieldProps["onReloadOptions"];
    reloadingOptions?: DynamicFormFieldProps["reloadingOptions"];
    failedOptions?: DynamicFormFieldProps["failedOptions"];
};

/** Children of a fieldset, rendered at the given form path. */
function FieldsetBody({ field, form, name, descriptorPath, ...rest }: FieldsetProps) {
    return <DynamicFormFields fields={field.subfields ?? []} form={form} parentPath={name} parentDescriptorPath={descriptorPath} {...rest} />;
}

/** A fieldset describing a single nested object. */
function FieldsetGroup(props: FieldsetProps) {
    const { field, form, name, help } = props;
    const label = field.label || field.field_name;
    const description = field.description || "";
    const { error } = form.getFieldState(name, form.formState);

    return (
        <fieldset className="col-span-2 flex flex-col gap-2 rounded-md border border-input p-3">
            <legend className="flex items-center gap-1 px-1 text-sm font-medium">
                {label}
                {isRequiredField(field) && <sup className="text-xs text-red-500">*</sup>}
                {help?.(field)}
            </legend>
            <FieldsetHint description={description} help={!!help} />
            <FieldsetBody {...props} />
            {error?.message && <span className="text-xs text-red-500">{error.message as string}</span>}
        </fieldset>
    );
}

/** A fieldset describing a repeatable list of nested objects. */
function RepeatableFieldset(props: FieldsetProps) {
    const { field, form, name, descriptorPath, help } = props;
    const label = field.label || field.field_name;
    const description = field.description || "";
    const subfields = field.subfields ?? [];
    const { fields: rows, append, remove, replace } = useFieldArray({ control: form.control, name });
    // `rows_from`: one row per value chosen in another field, kept in step with it, titled with the
    // value's option label; the rows are not added nor removed by hand.
    const follows = field.rows_from && field.row_key ? { from: field.rows_from, key: field.row_key } : null;
    const chosen = follows ? chosenValues(form.watch(follows.from as any)) : [];
    const chosenKey = chosen.join(",");
    useEffect(() => {
        if (!follows) return;
        const current = form.getValues(name);
        const next = followRows(current, chosen, follows.key, subfields);
        if (next !== current) replace(next);
    }, [chosenKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const rowTitle = (row: Record<string, unknown>, index: number) => {
        if (!follows) return `${label} #${index + 1}`;
        const value = String(row[follows.key] ?? "");
        const option = (props.dynamicOptions[follows.from] ?? []).find(item => String(item.value) === value);
        return option?.label ?? value;
    };
    const editable = !field.disabled && !follows;
    // An array-level error (a count bound, for instance); a row's own errors are
    // rendered by the child that carries them. React Hook Form files it under `root`
    // when rows have errors of their own.
    const { error } = form.getFieldState(name, form.formState);
    const arrayError = error as (FieldError & { root?: FieldError }) | undefined;
    const arrayMessage = arrayError?.root?.message ?? arrayError?.message;

    return (
        <fieldset className="col-span-2 flex flex-col gap-3 rounded-md border border-input p-3">
            <legend className="flex items-center gap-1 px-1 text-sm font-medium">
                {label}
                {isRequiredField(field) && <sup className="text-xs text-red-500">*</sup>}
                {help?.(field)}
            </legend>
            <FieldsetHint description={description} help={!!help} />

            {rows.length === 0 && <p className="text-xs text-gray-500">No entry yet.</p>}

            {rows.map((row, index) => (
                <div key={row.id} className="flex flex-col gap-2 rounded-md bg-muted/40 p-3">
                    <div className="flex items-center justify-between">
                        <span className={cn("text-xs font-medium text-gray-500", !follows && "uppercase")}>{rowTitle(row as Record<string, unknown>, index)}</span>
                        {editable && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-gray-400 hover:text-red-700"
                                onClick={() => remove(index)}
                                aria-label={`Remove ${label} #${index + 1}`}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                    <FieldsetBody
                        {...props}
                        name={`${name}.${index}`}
                        descriptorPath={descriptorPath}
                        backendFieldErrors={
                            follows
                                ? rowErrorsByKey(props.backendFieldErrors, name, String((row as Record<string, unknown>)[follows.key] ?? ""), index)
                                : props.backendFieldErrors
                        }
                    />
                </div>
            ))}

            {editable && (
                <div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="-ml-2 text-blue-600 hover:text-blue-800"
                        aria-label={`Add ${label}`}
                        onClick={() => append(buildDefaultValues(subfields))}
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                    </Button>
                </div>
            )}

            {arrayMessage && <span className="text-xs text-red-500">{arrayMessage}</span>}
            {/* An error the server files on the list itself (a row missing), not on one of its rows. */}
            {props.backendFieldErrors[name] && <span className="text-xs text-red-500">{props.backendFieldErrors[name]}</span>}
        </fieldset>
    );
}
